import { FC, useCallback } from "react";
import { IconButton, LocaleProvider, Menu, Portal, Text } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import { CASE_FILE_KIND, CaseDocumentFileModel, CaseDocumentModel, CaseModel } from "@shared/models";
import { getCaseFileSignedUrl } from "@containers/Cases/store/actions";
import { selectors } from "@containers/Cases/store";
import { CASE_DOCUMENT_INFO } from "@containers/Cases/constants";
import { useCaseDocumentActions } from "@containers/Cases/hooks";
import MenuIcon from "@assets/files/images/icons/menuIcon.svg?react";
import { AddIcon, ChevronLeftIcon, ExternalLinkIcon, ViewIcon } from "@shared/components/Icons";

import { CaseDocumentFileMenuItems } from "../CaseDocuments";
import { isFailedFile } from "../CaseDocuments/CaseDocuments.utils";

const UPLOADED_DOCUMENT_KINDS = [CASE_FILE_KIND.MEDICAL, CASE_FILE_KIND.NOTES];

const menuItemStyle = {
  "& [role=menuitem]": {
    p: 4,
    borderRadius: "8px",
  },
  "& [role=menuitem][data-highlighted]": {
    bg: "brand.primary/12",
  },
};

const ViewItemLabel: FC<{ name: string }> = ({ name }) => (
  <>
    <ViewIcon boxSize={5} color="brand.primary" />
    <Text ml={2} textStyle="overlayTitle" color="brand.primary">
      View {name}
    </Text>
  </>
);

interface ViewDocumentItemProps {
  caseDocument: CaseDocumentModel;
  onView: (file: CaseDocumentFileModel) => void;
}

const ViewDocumentItem: FC<ViewDocumentItemProps> = ({ caseDocument: { kind, files }, onView }) => {
  if (files.length === 1) {
    return (
      <Menu.Item value={`view-${kind}`} onClick={() => onView(files[0])} gap={0}>
        <ViewItemLabel name={kind === CASE_FILE_KIND.MEDICAL ? "Medical Summary" : CASE_DOCUMENT_INFO[kind].menuName} />
        <ExternalLinkIcon ml="auto" pl={4} boxSize={4} boxSizing="content-box" color="brand.primary" />
      </Menu.Item>
    );
  }

  // A submenu ignores its positioning prop and opens to the right, or to the left in RTL. The menu sits at the
  // page's right edge, so the submenu gets an RTL locale to open on the left, and its parts are set back to LTR.
  return (
    <LocaleProvider locale="ar">
      <Menu.Root>
        <Menu.TriggerItem gap={0} dir="ltr">
          <ViewItemLabel name={CASE_DOCUMENT_INFO[kind].menuName} />
          <ChevronLeftIcon ml="auto" pl={4} boxSize={5} boxSizing="content-box" color="brand.primary" />
        </Menu.TriggerItem>
        <Portal>
          <Menu.Positioner css={{ "& [dir]": { direction: "ltr" } }}>
            {/* The submenu starts at the item's edge: 7px clears the menu's padding and border, the rest is the gap. */}
            <Menu.Content mr="15px">
              <CaseDocumentFileMenuItems title={CASE_DOCUMENT_INFO[kind].menuName} files={files} onView={onView} />
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </LocaleProvider>
  );
};

interface AddDocumentItemProps {
  kind: CASE_FILE_KIND;
  onAdd?: () => void;
  /** Shown while the item is disabled */
  hint?: string;
}

const AddDocumentItem: FC<AddDocumentItemProps> = ({ kind, onAdd, hint }) => (
  <Menu.Item value={`add-${kind}`} disabled={!onAdd} onClick={onAdd} gap={0} flexWrap="wrap">
    <AddIcon boxSize="14px" m="3px" color="brand.modalHeaderText" />
    <Text ml={2} textStyle="overlayTitle" color="brand.modalHeaderText">
      Add {CASE_DOCUMENT_INFO[kind].menuName}
    </Text>
    {!onAdd && hint && (
      <Text w="100%" mt={1} pl={7} fontSize="12px" color="brand.emptyStateText">
        {hint}
      </Text>
    )}
  </Menu.Item>
);

interface CaseViewHeaderMenuProps {
  caseObj?: CaseModel | null;
}

const CaseViewHeaderMenu: FC<CaseViewHeaderMenuProps> = ({ caseObj }) => {
  const dispatch = useDispatch();
  const caseDocuments = useSelector(selectors.getCaseDocuments());

  const caseId = caseObj?.id;
  const { view, add, addHint } = useCaseDocumentActions(caseId);

  const handleWatchDocument = useCallback(() => {
    caseId && dispatch(getCaseFileSignedUrl.request(caseId));
  }, [caseId, dispatch]);

  const uploadedDocuments = caseDocuments.filter(({ kind }) => UPLOADED_DOCUMENT_KINDS.includes(kind));
  const documentsToView = uploadedDocuments
    .map((caseDocument) => ({ ...caseDocument, files: caseDocument.files.filter((file) => !isFailedFile(file)) }))
    .filter(({ files }) => files.length > 0);
  const kindsToAdd = UPLOADED_DOCUMENT_KINDS.filter(
    (kind) => !uploadedDocuments.some((caseDocument) => caseDocument.kind === kind),
  );

  return (
    <>
      <Menu.Root>
        <Menu.Trigger asChild>
          {/* v3 sizes every button svg to 20px; the menu glyph is drawn at its own 32px. */}
          <IconButton border="none" aria-label="Options" variant="outline" _icon={{ w: "auto", h: "auto" }}>
            <MenuIcon />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content css={menuItemStyle}>
              <Menu.Item value="view-psr" onClick={handleWatchDocument} gap={0}>
                <ViewItemLabel name={CASE_DOCUMENT_INFO[CASE_FILE_KIND.PSR].menuName} />
              </Menu.Item>
              {documentsToView.map((caseDocument) => (
                <ViewDocumentItem key={caseDocument.kind} caseDocument={caseDocument} onView={view} />
              ))}
              {kindsToAdd.map((kind) => (
                <AddDocumentItem key={kind} kind={kind} onAdd={add?.[kind]} hint={addHint} />
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </>
  );
};

export default CaseViewHeaderMenu;
